import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Grid, Card, CardContent, Typography, Fab, Modal, Box, CircularProgress, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AddItemForm from './AddItemForm';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AddIcon from '@mui/icons-material/Add';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CardTitleRow from './CardTitleRow';
import LoadErrorScreen from './LoadErrorScreen';
import './ItemList.css';
import './ItemCard.css';

const PAGE_SIZE = 100;
const PAGE_SIZE_MOBILE_INITIAL = 24; // меньше первая порция на мобильной — быстрее ответ и отрисовка
const PAGE_SIZE_MOBILE_NEXT = 48;   // размер подгрузки на мобильной

const ItemsList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [cartItemIds, setCartItemIds] = useState(new Set());
  const [addingCartId, setAddingCartId] = useState(null);
  const [branchAddressByKey, setBranchAddressByKey] = useState({});
  const loadMoreSentinelRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const buildItemsUrl = useCallback((cursor = null, limit = null) => {
    const searchParams = new URLSearchParams(location.search);
    const dateFrom = searchParams.get('dateFrom');
    const dateTo = searchParams.get('dateTo');
    const branchId = searchParams.get('branchId');
    const effectiveLimit = limit != null ? limit : (cursor ? (isMobile ? PAGE_SIZE_MOBILE_NEXT : PAGE_SIZE) : (isMobile ? PAGE_SIZE_MOBILE_INITIAL : PAGE_SIZE));
    let url = `${process.env.REACT_APP_SERVER_URL}/api/items?visibility=true&moderation=approved&limit=${effectiveLimit}`;
    if (dateFrom) url += `&from=${encodeURIComponent(dateFrom)}`;
    if (dateTo) url += `&to=${encodeURIComponent(dateTo)}`;
    if (branchId) url += `&branchId=${encodeURIComponent(branchId)}`;
    if (cursor) url += `&cursor=${encodeURIComponent(cursor)}`;
    return url;
  }, [location.search, isMobile]);

  const fetchCart = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/cart`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const ids = (data.items || []).map((i) => (i.itemId && (i.itemId._id || i.itemId)) || i.itemId).filter(Boolean);
        setCartItemIds(new Set(ids.map(String)));
      }
    } catch (e) {
      console.error('Ошибка загрузки корзины:', e);
    }
  }, []);

  const fetchAllItems = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    setNextCursor(null);
    try {
      const initialLimit = isMobile ? PAGE_SIZE_MOBILE_INITIAL : PAGE_SIZE;
      const url = buildItemsUrl(null, initialLimit);
      const response = await fetch(url);
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Сервер вернул не JSON:', text.substring(0, 200));
        throw new Error('Сервер вернул неверный формат ответа. Проверьте настройки сервера.');
      }
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Ошибка загрузки товаров' }));
        throw new Error(errorData.message || 'Ошибка загрузки товаров');
      }
      const data = await response.json();
      const itemsArray = data.items || (Array.isArray(data) ? data : []) || [];
      setItems(Array.isArray(itemsArray) ? itemsArray : []);
      setNextCursor(data.nextCursor || null);
    } catch (error) {
      console.error('Ошибка загрузки товаров:', error);
      setLoadError(true);
      setItems([]);
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  }, [buildItemsUrl, isMobile]);

  const fetchMoreItems = useCallback(async () => {
    if (!nextCursor || loadingMore || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const url = buildItemsUrl(nextCursor);
      const response = await fetch(url);
      if (!response.ok) return;
      const data = await response.json();
      const newItems = data.items || (Array.isArray(data) ? data : []) || [];
      setItems((prev) => [...prev, ...newItems]);
      setNextCursor(data.nextCursor || null);
    } catch (error) {
      console.error('Ошибка подгрузки товаров:', error);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, buildItemsUrl]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
    fetchAllItems();
    if (token) fetchCart();

    const handleScroll = () => {
      setShowScrollToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.search, fetchCart, fetchAllItems]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/admin/branches/public`);
        if (!res.ok) return;
        const list = await res.json();
        const map = {};
        (list || []).forEach((b) => {
          const key = b.branchId || b._id;
          if (key) map[key] = [b.city, b.address].filter(Boolean).join(', ') || key;
        });
        setBranchAddressByKey(map);
      } catch (e) {
        console.error('Ошибка загрузки филиалов:', e);
      }
    };
    fetchBranches();
  }, []);

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !nextCursor) return;
    const rootMargin = isMobile ? '400px' : '200px';
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || loadingMore) return;
        fetchMoreItems();
      },
      { rootMargin, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [nextCursor, loadingMore, fetchMoreItems, isMobile]);

  // Резервная подгрузка по скроллу на мобильных (на части устройств IntersectionObserver срабатывает нестабильно)
  useEffect(() => {
    if (!isMobile || !nextCursor || loadingMore) return;
    const checkScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      if (scrollHeight - scrollTop - clientHeight < 400) fetchMoreItems();
    };
    window.addEventListener('scroll', checkScroll, { passive: true });
    return () => window.removeEventListener('scroll', checkScroll);
  }, [isMobile, nextCursor, loadingMore, fetchMoreItems]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const legacySearch = (searchParams.get('search') || '').trim();
    if (!legacySearch) return;
    const params = new URLSearchParams(location.search);
    params.delete('search');
    params.set('q', legacySearch);
    params.set('type', 'item');
    navigate(`/search?${params.toString()}`, { replace: true });
  }, [location.search, navigate]);

  // Функция для правильной обработки URL изображений
  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    if (typeof photo === 'string') {
      const trimmed = photo.trim();
      if (!trimmed) return null;
      
      // Если уже полный URL (S3 или другой)
      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        return trimmed;
      }
      // Если относительный путь
      if (trimmed.startsWith('/')) {
        return `${process.env.REACT_APP_SERVER_URL}${trimmed}`;
      }
      // Если путь без слеша в начале
      return `${process.env.REACT_APP_SERVER_URL}/${trimmed}`;
    }
    return null;
  };

  // Функция для нормализации и удаления дубликатов фотографий
  const normalizePhotos = (photos) => {
    if (!photos || !Array.isArray(photos) || photos.length === 0) {
      return [];
    }

    const seenUrls = new Set();
    const normalized = [];

    // Сначала нормализуем все URL
    const normalizedUrls = photos
      .filter(photo => photo && typeof photo === 'string' && photo.trim() !== '')
      .map(photo => getPhotoUrl(photo))
      .filter(url => url !== null);

    // Затем убираем дубликаты, сохраняя порядок
    for (const url of normalizedUrls) {
      if (!seenUrls.has(url)) {
        seenUrls.add(url);
        normalized.push(url);
      }
    }

    return normalized;
  };


  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAddToCart = async (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    const token = localStorage.getItem('token');
    if (!token) {
      if (window.confirm('Для добавления в корзину нужно войти. Перейти на страницу входа?')) {
        navigate('/login', { state: { redirectTo: `${location.pathname}${location.search}` } });
      }
      return;
    }
    if (addingCartId || cartItemIds.has(String(item._id))) return;
    setAddingCartId(item._id);
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/cart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ itemId: item._id, quantity: 1 }),
      });
      if (response.ok) {
        setCartItemIds((prev) => new Set([...prev, String(item._id)]));
      }
    } catch (err) {
      console.error('Ошибка добавления в корзину:', err);
    } finally {
      setAddingCartId(null);
    }
  };

  if (loadError) {
    return <LoadErrorScreen />;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', px: 0.25, mx: 0, mt: 2 }}> {/* Блокируем горизонтальную прокрутку на мобильных */}
      {/* Модальное окно */}
      <Modal
        open={showAddForm}
        onClose={() => setShowAddForm(false)}
        aria-labelledby="Добавить новый товар"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 600,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxHeight: '90vh',
            overflow: 'auto',
          }}
        >
          <AddItemForm
            onItemAdded={() => {
              fetchAllItems();
              setShowAddForm(false);
            }}
            onClose={() => setShowAddForm(false)}
          />
        </Box>
      </Modal>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress sx={{ color: '#8B7D6B' }} />
        </Box>
      ) : (
        <Grid container spacing={1} className="items-grid-container" sx={{ px: 0.5, overflowX: 'hidden', maxWidth: '100%' }}>
          {items && items.length > 0 ? (
            items.map((item, index) => {
              const isAboveFold = index < 6;
              return (
              <Grid item xs={6} sm={6} md={4} lg={true} key={item._id || item.inventoryNumber} className="item-grid-item">
                <Card component={Link} to={`/item/${item.inventoryNumber || item._id}`} className="card-container">
                  <Box sx={{ position: 'relative' }}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleAddToCart(e, item)}
                      disabled={!!addingCartId}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 1,
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        '&:hover': { backgroundColor: '#fff' },
                      }}
                      aria-label="Добавить в корзину"
                    >
                      <ShoppingCartIcon
                        sx={{
                          color: cartItemIds.has(String(item._id)) ? '#d32f2f' : undefined,
                        }}
                      />
                    </IconButton>
                    {(() => {
                      const normalizedPhotos = normalizePhotos(item.photos);
                      if (normalizedPhotos.length > 0) {
                        return (
                          <div className="slider-image-container">
                            <img
                              src={normalizedPhotos[0]}
                              alt={`Фото товара ${item.title}`}
                              className="card-image"
                              loading={isAboveFold ? 'eager' : 'lazy'}
                              decoding="async"
                              fetchPriority={isAboveFold ? 'high' : undefined}
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/300';
                              }}
                            />
                          </div>
                        );
                      }
                      return (
                        <div className="slider-image-container">
                          <img
                            src="https://via.placeholder.com/300"
                            alt="Заглушка"
                            className="card-image"
                            loading={isAboveFold ? 'eager' : 'lazy'}
                            decoding="async"
                            fetchPriority={isAboveFold ? 'high' : undefined}
                          />
                        </div>
                      );
                    })()}
                  </Box>
                  <CardContent className="card-content">
                    <CardTitleRow type="item" entityId={item._id}>
                      {item.title}
                    </CardTitleRow>
                    <Typography className="card-price" sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '0.85rem' }}>
                      {item.price} ₽/сутки
                    </Typography>
                    {(item.branchId && branchAddressByKey[item.branchId]) && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', mt: 0.5 }}>
                        📍 {branchAddressByKey[item.branchId]}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
            })
          ) : (
            <Typography className="no-items-message">На данный момент товаров нет</Typography>
          )}
          {nextCursor && items && items.length > 0 && (
            <Grid item xs={12} sx={{ py: 0, px: 0, mx: 0 }}>
              <div ref={loadMoreSentinelRef} aria-hidden="true" style={{ height: 2, minHeight: 2 }} />
            </Grid>
          )}
          {loadingMore && (
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={32} sx={{ color: '#8B7D6B' }} />
            </Grid>
          )}
        </Grid>
      )}

      {/* Кнопка прокрутки вверх */}
      {showScrollToTop && (
        <Fab size="medium" onClick={handleScrollToTop} className="scroll-to-top">
          <KeyboardArrowUpIcon />
        </Fab>
      )}

      {/* Кнопка добавления товара - временно скрыта */}
      {/* {isAuthenticated && (
        <Fab size="large" onClick={() => setShowAddForm(true)} className="add-item-button">
          <AddIcon />
        </Fab>
      )} */}
    </Box>
  );
};

export default ItemsList;
